export const FEATURED_VEHICLE_SELECTION_MODES = Object.freeze({
  AUTOMATIC: "automatic",
  MANUAL: "manual",
});

export const MAX_FEATURED_VEHICLE_SELECTION = 24;

const cleanKey = (value) => String(value ?? "").trim();

const uniqueKeys = (values, maximum = MAX_FEATURED_VEHICLE_SELECTION) => {
  const result = [];
  const seen = new Set();
  (Array.isArray(values) ? values : []).forEach((value) => {
    const key = cleanKey(value);
    if (!key || seen.has(key) || result.length >= maximum) return;
    seen.add(key);
    result.push(key);
  });
  return result;
};

export const getFeaturedVehicleIdentityKeys = (item) => {
  const general = item?.general_detail || {};
  const vehicle = item?.inventory_vehicle || {};
  return uniqueKeys(
    [
      item?.documentId,
      general.document_id,
      item?.id,
      item?.inventoryId,
      item?.stock,
      item?.stockNumber,
      item?.stock_number,
      general.stock_number,
      item?.vin,
      general.vin,
      vehicle.vin,
    ],
    32
  );
};

export const getFeaturedVehicleKey = (item) =>
  getFeaturedVehicleIdentityKeys(item)[0] || "";

const resolveLegacyKeys = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return [];
  return (
    value.vehicleKeys ||
    value.selectedVehicleKeys ||
    value.selectedVehicleIds ||
    value.featuredVehicleIds ||
    value.vehicleIds ||
    []
  );
};

export const normalizeFeaturedVehicleSelection = (value, legacyData = null) => {
  const source =
    value && typeof value === "object" && !Array.isArray(value) ? value : {};
  const sourceKeys = resolveLegacyKeys(source);
  const legacyKeys = resolveLegacyKeys(legacyData);
  const vehicleKeys = uniqueKeys(sourceKeys.length ? sourceKeys : legacyKeys);
  const requestedMode = cleanKey(source.mode).toLowerCase();
  const mode =
    requestedMode === FEATURED_VEHICLE_SELECTION_MODES.MANUAL
      ? FEATURED_VEHICLE_SELECTION_MODES.MANUAL
      : requestedMode === FEATURED_VEHICLE_SELECTION_MODES.AUTOMATIC
        ? FEATURED_VEHICLE_SELECTION_MODES.AUTOMATIC
        : vehicleKeys.length > 0
          ? FEATURED_VEHICLE_SELECTION_MODES.MANUAL
          : FEATURED_VEHICLE_SELECTION_MODES.AUTOMATIC;

  return { mode, vehicleKeys };
};

export const isManualFeaturedVehicleSelection = (value) =>
  normalizeFeaturedVehicleSelection(value).mode ===
  FEATURED_VEHICLE_SELECTION_MODES.MANUAL;

export const filterAndOrderFeaturedVehicles = (items, selection) => {
  const source = Array.isArray(items) ? items : [];
  const normalized = normalizeFeaturedVehicleSelection(selection);
  if (normalized.mode !== FEATURED_VEHICLE_SELECTION_MODES.MANUAL) {
    return source;
  }

  const itemByKey = new Map();
  source.forEach((item) => {
    getFeaturedVehicleIdentityKeys(item).forEach((key) => {
      if (!itemByKey.has(key)) itemByKey.set(key, item);
    });
  });

  const emitted = new Set();
  return normalized.vehicleKeys.reduce((result, key) => {
    const item = itemByKey.get(key);
    if (!item) return result;
    const canonicalKey = getFeaturedVehicleKey(item) || key;
    if (emitted.has(canonicalKey)) return result;
    emitted.add(canonicalKey);
    result.push(item);
    return result;
  }, []);
};

const titleCase = (value) => {
  const text = cleanKey(value);
  if (!text) return "";
  return text
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
};

export const buildFeaturedVehicleOption = (item) => {
  const general = item?.general_detail || {};
  const vehicle = item?.inventory_vehicle || {};
  const key = getFeaturedVehicleKey(item);
  if (!key) return null;

  const year = vehicle.year ?? item?.year ?? general.year ?? "";
  const make = titleCase(vehicle.make ?? item?.make ?? general.make);
  const model = cleanKey(vehicle.model ?? item?.model ?? general.model);
  const trim = cleanKey(vehicle.trim ?? item?.trim ?? general.trim);
  const title = [year, make, model, trim].filter(Boolean).join(" ").trim();
  const stock = cleanKey(
    item?.stock ?? item?.stockNumber ?? item?.stock_number ?? general.stock_number
  );
  const vin = cleanKey(item?.vin ?? general.vin ?? vehicle.vin);
  const status = cleanKey(
    item?.statusName ??
      item?.status ??
      vehicle.vehicle_status?.status_name ??
      general.status
  );
  const metadata = [stock ? `#${stock}` : "", vin, status]
    .filter(Boolean)
    .join(" · ");

  return {
    key,
    value: key,
    label: title || stock || vin || key,
    metadata,
    vehicle: item,
  };
};

export const buildFeaturedVehicleOptions = (items) =>
  (Array.isArray(items) ? items : [])
    .map(buildFeaturedVehicleOption)
    .filter(Boolean)
    .filter(
      (option, index, options) =>
        options.findIndex((candidate) => candidate.key === option.key) === index
    );
